import React from 'react'
import { useContext, useEffect, useState } from 'react';
import { Context } from '../../Context/Context';
import { stripeProductPaymentLink, stripeProductPaymentLinkFunder } from '../../Constants';
import { FIRM, canSubscribeApi, getSubscriptionPriceApi } from 'Constants';
import { toast } from 'react-toastify';
import { Check, Info } from 'lucide-react';

// Design 18c "Membership plans" - two plan cards (Basic 1fr / Premium 1.3fr).
// Copy, pricing (from Stripe) and all subscribe/manage behavior are unchanged;
// only the visual treatment follows the design.

const BRASS = '#8a6231';
const planNameStyle = { font: "600 15px/1 'IBM Plex Sans',sans-serif", textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: '6px' };
const taglineStyle = { margin: '0 0 18px', font: "400 13px/1.4 'IBM Plex Sans',sans-serif", color: 'rgba(20,33,61,.5)' };
const priceStyle = { font: "400 34px/1 'Spectral',serif", color: '#14213d' };
const perStyle = { font: "400 13px/1 'IBM Plex Sans',sans-serif", color: 'rgba(20,33,61,.5)' };
const sectionLabelStyle = { font: "600 11px/1 'IBM Plex Mono',monospace", letterSpacing: '.08em', textTransform: 'uppercase' };
const currentPlanTag = (
    <span style={{ font: "600 10px/1 'IBM Plex Mono',monospace", letterSpacing: '.06em', textTransform: 'uppercase', color: '#fffdf8', background: '#14213d', padding: '3px 8px', borderRadius: '3px' }}>Current plan</span>
);

const BASIC_FEATURES = [
    'Limited Access to the Latest Cases',
    'Streamlined Case Approvals',
    'Unlimited Case Requests',
    'Unlimited Leads',
    'Add Legal Assistants',
    'Limited Data Access',
];

const PREMIUM_FEATURES = [
    { title: 'Access to the Latest Cases', desc: "Stay updated with immediate access to newly posted case leads, ensuring you're always the first to access right after client posts." },
    { title: 'Priority for Time-Sensitive Cases (SOS)', desc: 'Gain exclusive access to high-priority, time-sensitive cases requiring urgent attention.' },
    { title: 'Automatic Pre-Approval Listing', desc: 'Subscribers are automatically added to the pre-approved attorney list, making your profile visible to clients for instant case-viewing approvals from clients.' },
    { title: 'Streamlined Case Approvals', desc: 'Clients can pre-approve you to view their case, moving it directly to your "approved" tab, enabling you to perform conflicts checks and review cases immediately.' },
    { title: 'Unlimited Case Requests', desc: 'Send unlimited requests to clients to view their cases, maximizing your opportunities to connect and engage.' },
    { title: 'Unlimited Leads', desc: 'Access an unlimited number of leads associated with cases, broadening your opportunities to secure cases and remove clients from further seeking an attorney.' },
    { title: 'Premium Data Access', desc: 'Subscribers gain exclusive access to premium client data insights for each case, providing detailed information to quickly guide your decisions.' },
    { title: 'Add Legal Assistants', desc: 'Subscribers can expand their team by adding assistants with controlled access of the case details and notifications.', notForFunder: true },
];

function SubscriptionPlans({onSubmit}) {
    const {makeApiCallFunc, userDetails} = useContext(Context);
    const [priceObj, setPriceObj] = useState({});
    const [showInfo, setShowInfo] = useState(false);
    const [subscriptionData, setSubscriptionData] = useState({});
    const isActive = !!userDetails?.isActiveSubscription;
    const [checking, setChecking] = useState(false);

    // Premium is for verified law firm accounts. Checked on the page first,
    // then again with the server (fresh from the database) right before the
    // Stripe checkout opens.
    const localBlock = !userDetails?._id ? 'Please sign in to subscribe.'
        : (userDetails?.role !== FIRM || userDetails?.isUserAssistant) ? 'Premium is available for law firm accounts.'
        : !userDetails?.verified ? "Your profile needs to be verified before you can subscribe. We'll email you once it's approved."
        : null;
    const subscribe = () => {
        if (checking) return;
        if (localBlock) { toast.error(localBlock, { toastId: 'subscribe-block' }); return; }
        setChecking(true);
        makeApiCallFunc(canSubscribeApi, {},
            ({data}) => {
                setChecking(false);
                if (!data?.ok) { toast.error(data?.message || 'You can’t subscribe right now.', { toastId: 'subscribe-block' }); return; }
                // client_reference_id ties the Stripe checkout back to this exact
                // user - it's immutable (unlike email, which the customer can edit
                // at Stripe's checkout page), so the webhook can match reliably.
                const base = userDetails?.isUserFunder ? stripeProductPaymentLinkFunder : stripeProductPaymentLink;
                window.location.assign(`${base}?prefilled_email=${encodeURIComponent(userDetails?.email)}&client_reference_id=${userDetails?._id}`);
            },
            (error) => {
                setChecking(false);
                toast.error(error?.response?.data?.message || 'Could not check your account. Please try again.', { toastId: 'subscribe-block' });
            },
            null, true);
    };
    const audience = userDetails?.isUserFunder ? "Litigation Funders" : "Professionals";

    useEffect(() => {
        makeApiCallFunc(getSubscriptionPriceApi,
            {
                isUserFunder: userDetails?.isUserFunder
            },
            ({data}) => {
                setSubscriptionData(data);
                const priceInDollars = (data?.price?.unit_amount || 0) / 100;
                setPriceObj({"price":priceInDollars.toFixed(2), "currency":data?.price?.currency });
            },
            (error) => {
                console.log(error)
            }
        )
    }
    , [])

    const subscribeButtonClass = 'w-full text-center transition-colors bg-tan hover:bg-tanDark';
    const subscribeButtonStyle = { font: "500 13px/1 'IBM Plex Sans',sans-serif", color: '#fffdf8', border: `1.5px solid ${BRASS}`, borderRadius: '5px', padding: '13px', marginBottom: '22px', cursor: 'pointer' };

  return (
    <div className='grid grid-cols-1 lg:grid-cols-[1fr_1.3fr] items-start font-plexsans' style={{ gap: '22px' }}>

        {/* Basic */}
        <div className='bg-white' style={{ border: '1px solid rgba(20,33,61,.14)', borderRadius: '8px', padding: '30px 28px' }}>
            <div className='flex items-center' style={{ gap: '8px', ...planNameStyle, color: 'rgba(20,33,61,.45)' }}>
                <span>Basic Plan (Free)</span>
                {!isActive && currentPlanTag}
            </div>
            <p style={taglineStyle}>For&nbsp;{audience}</p>
            <div className='flex items-baseline' style={{ gap: '6px', marginBottom: '24px' }}>
                <span style={priceStyle}>$0.00</span>
                <span style={perStyle}>per month</span>
            </div>
            <div style={{ ...sectionLabelStyle, color: 'rgba(20,33,61,.4)', marginBottom: '14px' }}>What's included</div>
            <div className='flex flex-col' style={{ gap: '12px' }}>
                {BASIC_FEATURES.map((f) => (
                    <div key={f} className='flex items-start' style={{ gap: '9px' }}>
                        <Check size={14} strokeWidth={2.4} color={BRASS} className='flex-none' style={{ marginTop: '2px' }} />
                        <span style={{ font: "400 13.5px/1.5 'IBM Plex Sans',sans-serif", color: '#14213d' }}>{f}</span>
                    </div>
                ))}
            </div>
        </div>

        {/* Premium */}
        <div className='relative bg-white' style={{ border: `1.5px solid ${BRASS}`, borderRadius: '8px', padding: '30px 28px', boxShadow: '0 6px 22px rgba(20,33,61,.1)' }}>
            {isActive &&
                <div className='flex items-center flex-wrap' style={{ gap: '8px', padding: '9px 16px', borderRadius: '5px', background: 'rgba(58,125,79,.08)', border: '1px solid rgba(58,125,79,.3)', marginBottom: '20px' }}>
                    <Check size={14} strokeWidth={2.4} color="#3a7d4f" className='flex-none' />
                    <span style={{ font: "500 12.5px/1.3 'IBM Plex Sans',sans-serif", color: '#14213d' }}>Your firm has an active subscription</span>
                    <span
                        onClick={onSubmit}
                        className='cursor-pointer hover:text-tanDark'
                        style={{ marginLeft: 'auto', font: "600 12px/1 'IBM Plex Sans',sans-serif", color: BRASS, textDecoration: 'underline' }}
                    >Manage plan</span>
                </div>
            }
            <div className='flex items-center' style={{ gap: '8px', marginBottom: '6px' }}>
                <span style={{ ...planNameStyle, marginBottom: 0, color: BRASS }}>{subscriptionData.name}</span>
                {isActive && currentPlanTag}
            </div>
            <p style={taglineStyle}>For&nbsp;{audience}</p>
            <div className='flex items-baseline' style={{ gap: '6px', marginBottom: '22px' }}>
                <span style={priceStyle}>
                    {priceObj.currency === "usd" && "$"}
                    {priceObj.price}
                </span>
                <span style={perStyle}>per month</span>
            </div>

            {isActive ?
                <div className='relative'>
                    <button
                        type="button"
                        onClick={onSubmit}
                        onMouseEnter={() => setShowInfo(true)}
                        onMouseLeave={() => setShowInfo(false)}
                        className='w-full flex items-center justify-center transition-colors hover:bg-[rgba(138,98,49,0.06)]'
                        style={{ gap: '6px', font: "500 13px/1 'IBM Plex Sans',sans-serif", color: BRASS, background: 'transparent', border: `1.5px solid ${BRASS}`, borderRadius: '5px', padding: '13px', marginBottom: '22px' }}
                    >
                        Click To Manage Your Active Plan
                        <Info size={14} strokeWidth={2} />
                    </button>
                    {showInfo &&
                        <div className='absolute left-0 right-0 z-10' style={{ top: '48px', background: '#fffdf8', border: '1px solid rgba(138,98,49,.3)', borderRadius: '5px', padding: '10px 12px', font: "400 12.5px/1.5 'IBM Plex Sans',sans-serif", color: '#14213d', boxShadow: '0 6px 18px rgba(20,33,61,.12)' }}>
                            By clicking here you can Cancel/Renew your current plan, Edit payment methods, Edit Billing information, See invoice history, Download invoice receipt and more.
                        </div>
                    }
                </div>
                :
                <button
                    type="button"
                    onClick={subscribe}
                    aria-disabled={checking || undefined}
                    title={localBlock || undefined}
                    className={`${subscribeButtonClass} ${checking ? 'opacity-70 !cursor-wait' : ''}`}
                    style={subscribeButtonStyle}
                >{checking ? 'Checking…' : 'Subscribe'}</button>
            }

            <div style={{ height: '1px', background: 'rgba(20,33,61,.1)', marginBottom: '18px' }}></div>
            <div style={{ ...sectionLabelStyle, color: BRASS, marginBottom: '16px' }}>What's included</div>
            <div className='flex flex-col' style={{ gap: '16px' }}>
                {PREMIUM_FEATURES.filter((f) => !(f.notForFunder && userDetails?.isUserFunder)).map((f) => (
                    <div key={f.title}>
                        <div style={{ font: "600 13.5px/1.3 'IBM Plex Sans',sans-serif", color: '#14213d', marginBottom: '3px' }}>{f.title}</div>
                        <div style={{ font: "400 12.5px/1.5 'IBM Plex Sans',sans-serif", color: 'rgba(20,33,61,.55)' }}>{f.desc}</div>
                    </div>
                ))}
            </div>
        </div>
    </div>
  )
}

export default SubscriptionPlans
